import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { Gavel, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function VendorAuctions() {
  const { vendorFetch } = useVendorAuth();
  const [auctions, setAuctions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bid, setBid] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [timer, setTimer] = useState(0);

  useEffect(() => { vendorFetch('/partner/auctions').then(d => setAuctions(d.items || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!detail) return;
    setTimer(detail.time_remaining_seconds || 0);
    const id = setInterval(() => setTimer(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [detail?.id]);

  const openAuction = async (a) => {
    setSelected(a);
    const d = await vendorFetch(`/partner/auctions/${a.id}`).catch(() => null);
    setDetail(d);
    setBid((d?.item_lines || []).map(l => ({ item: l.item || l.description, quantity: l.quantity || 1, unit_price: '' })));
  };

  const placeBid = async () => {
    setPlacing(true);
    try {
      const r = await vendorFetch(`/partner/auctions/${selected.id}/bid`, {
        method: 'POST', body: JSON.stringify({ line_bids: bid.map(l => ({ ...l, unit_price: parseFloat(l.unit_price) || 0 })) }),
      });
      toast.success(`Bid accepted! You are Rank ${r.new_rank} of ${detail?.total_bidders || '?'} bidders.`);
      const d = await vendorFetch(`/partner/auctions/${selected.id}`);
      setDetail(d);
      setTimer(d.time_remaining_seconds || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bid rejected'); }
    setPlacing(false);
  };

  const fmtTime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (selected && detail) return (
    <div className="max-w-2xl space-y-5" data-testid="auction-bid-page">
      <button onClick={() => { setSelected(null); setDetail(null); }} className="text-xs text-slate-500 hover:text-[#3b4872]">← Back</button>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-bold text-xl">{detail.auction_no} — {detail.title}</div>
          <div className="text-sm text-slate-500">{detail.category}</div>
        </div>
        <div className={`text-right shrink-0 ${detail.status === 'live' ? 'text-emerald-600' : 'text-slate-400'}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider">{detail.status}</div>
          {detail.status === 'live' && timer > 0 && (
            <div className="text-2xl font-extrabold flex items-center gap-1.5"><Clock size={18} />{fmtTime(timer)}</div>
          )}
        </div>
      </div>
      {detail.my_rank && (
        <div className="bg-[#3b4872]/10 border border-[#3b4872]/20 rounded-sm p-4">
          <div className="text-xs font-bold text-[#3b4872] uppercase tracking-wider">Your Current Position</div>
          <div className="text-3xl font-extrabold text-[#3b4872] mt-1">
            Rank {detail.my_rank}
            {detail.total_bidders && <span className="text-lg font-semibold text-[#3b4872]/60"> of {detail.total_bidders} bidders</span>}
          </div>
          {detail.my_bid && <div className="text-xs text-slate-500 mt-1">Your latest bid: ₹{detail.my_bid.total_value?.toLocaleString('en-IN') || '—'}</div>}
        </div>
      )}
      {detail.status === 'live' && timer > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          <div className="font-bold text-sm">Place a Lower Bid</div>
          {bid.map((l, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 text-xs text-slate-700">{l.item} × {l.quantity}</div>
              <input type="number" placeholder="Unit price (₹)" value={l.unit_price}
                onChange={e => { const b = [...bid]; b[i] = { ...b[i], unit_price: e.target.value }; setBid(b); }}
                className="w-36 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]" />
            </div>
          ))}
          {detail.rules?.min_decrement_pct && (
            <div className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-sm">
              Each bid must be at least {detail.rules.min_decrement_pct}% lower than your previous bid.
            </div>
          )}
          <button onClick={placeBid} disabled={placing}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] text-white rounded-sm text-sm font-bold disabled:opacity-50" data-testid="place-bid-btn">
            {placing ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
            {placing ? 'Placing…' : 'Place Bid'}
          </button>
        </div>
      )}
      {(detail.status === 'closed' || detail.status === 'awarded' || timer === 0) && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-slate-500 text-sm text-center">Auction closed. Results will be communicated by Ellora EPC procurement.</div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="vendor-auctions-page">
      <div><h1 className="font-bold text-xl text-slate-900">Auctions</h1><p className="text-sm text-slate-500 mt-0.5">Reverse auctions you are invited to.</p></div>
      {auctions.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <Gavel size={28} className="mx-auto mb-3 opacity-40" /><div className="font-semibold">No auctions yet</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
          {auctions.map(a => (
            <button key={a.id} onClick={() => openAuction(a)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left" data-testid={`auction-row-${a.auction_no}`}>
              <div>
                <div className="font-semibold text-sm">{a.auction_no} — {a.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{a.category} · {a.item_lines?.length || 0} items</div>
              </div>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm ml-4 shrink-0 ${a.status === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{a.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
